import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Input, List, Modal, Popconfirm, Typography, message } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import api from '../common/api';
import { useCashFlow } from '../common/CashFlowContext';

function SimpleInputModal({ open, title, initial = '', placeholder, onClose, onSubmit }) {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setValue(initial); }, [open, initial]);

  const handleOk = async () => {
    const v = value.trim();
    if (!v) return message.error('이름을 입력해 주세요');
    setSaving(true);
    try {
      await onSubmit(v);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} title={title} onOk={handleOk} onCancel={onClose} confirmLoading={saving} destroyOnClose>
      <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} autoFocus />
    </Modal>
  );
}

export default function SettingsScreen() {
  const { currentUser, currentUserId, refreshUsers, setCurrentUserId } = useCashFlow();
  const [categories, setCategories] = useState([]);
  const [methods, setMethods] = useState([]);
  const [renameOpen, setRenameOpen] = useState(false);
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [addMethodOpen, setAddMethodOpen] = useState(false);

  const fetchLists = useCallback(async () => {
    const [c, m] = await Promise.all([api.get('/categories'), api.get('/methods')]);
    setCategories(c.data);
    setMethods(m.data);
  }, []);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const renameUser = async (name) => {
    await api.patch(`/users/${currentUserId}/name`, { name });
    await refreshUsers();
  };

  const addCategory = async (name) => {
    await api.post('/categories', { name });
    await fetchLists();
  };

  const addMethod = async (name) => {
    await api.post('/methods', { user_id: currentUserId, name });
    await fetchLists();
  };

  const deleteCategory = async (id) => {
    await api.delete(`/categories/${id}`);
    await fetchLists();
  };

  const deleteMethod = async (id) => {
    await api.delete(`/methods/${id}`);
    await fetchLists();
  };

  const logout = () => {
    setCurrentUserId(null);
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <Typography.Title level={5} style={{ color: '#888', fontSize: 13, margin: '8px 0' }}>내 정보</Typography.Title>
      <Card bodyStyle={{ padding: 0 }} style={{ marginBottom: 16 }}>
        <List>
          <List.Item
            actions={[<Button type="link" onClick={() => setRenameOpen(true)} key="rename">변경</Button>]}
          >
            <List.Item.Meta title="내 이름" description={currentUser?.name || '-'} />
          </List.Item>
          <List.Item
            actions={[<Button type="link" danger onClick={logout} key="logout">유저 변경</Button>]}
          >
            <List.Item.Meta title="계정" description="다른 유저로 바꾸기" />
          </List.Item>
        </List>
      </Card>

      <Typography.Title level={5} style={{ color: '#888', fontSize: 13, margin: '8px 0' }}>카테고리</Typography.Title>
      <Card bodyStyle={{ padding: 0 }} style={{ marginBottom: 16 }}>
        <List
          dataSource={categories}
          renderItem={(c) => (
            <List.Item
              actions={[
                <Popconfirm key="del" title="삭제할까요?" onConfirm={() => deleteCategory(c.id)} okText="삭제" cancelText="취소">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta title={c.name} />
            </List.Item>
          )}
        />
        <div style={{ padding: 12 }}>
          <Button type="dashed" block icon={<PlusOutlined />} onClick={() => setAddCatOpen(true)}>
            카테고리 추가
          </Button>
        </div>
      </Card>

      <Typography.Title level={5} style={{ color: '#888', fontSize: 13, margin: '8px 0' }}>결제수단</Typography.Title>
      <Card bodyStyle={{ padding: 0 }} style={{ marginBottom: 16 }}>
        <List
          dataSource={methods}
          renderItem={(m) => (
            <List.Item
              actions={[
                <Popconfirm key="del" title="삭제할까요?" onConfirm={() => deleteMethod(m.id)} okText="삭제" cancelText="취소">
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>,
              ]}
            >
              <List.Item.Meta title={m.name} />
            </List.Item>
          )}
        />
        <div style={{ padding: 12 }}>
          <Button type="dashed" block icon={<PlusOutlined />} onClick={() => setAddMethodOpen(true)}>
            결제수단 추가
          </Button>
        </div>
      </Card>

      <SimpleInputModal
        open={renameOpen}
        title="이름 변경"
        initial={currentUser?.name || ''}
        placeholder="새 이름 입력"
        onClose={() => setRenameOpen(false)}
        onSubmit={renameUser}
      />
      <SimpleInputModal
        open={addCatOpen}
        title="카테고리 추가"
        placeholder="카테고리 이름"
        onClose={() => setAddCatOpen(false)}
        onSubmit={addCategory}
      />
      <SimpleInputModal
        open={addMethodOpen}
        title="결제수단 추가"
        placeholder="결제수단 이름"
        onClose={() => setAddMethodOpen(false)}
        onSubmit={addMethod}
      />
    </div>
  );
}
